import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthShell from '../../components/onboarding/AuthShell';
import CompanyStep from '../../components/onboarding/CompanyStep';
import TeamStep from '../../components/onboarding/TeamStep';
import TrialStep from '../../components/onboarding/TrialStep';
import GetStartedChecklist from '../../components/onboarding/GetStartedChecklist';
import { useAuth } from '../../context/AuthContext';
import { errorMessage } from '../../utils/errorMessage';
import * as onboarding from '../../services/onboardingService';
import styles from '../../components/onboarding/Onboarding.module.css';

const COPY = {
  company: (name) => ({ title: `Set up ${name}`, subtitle: 'This sets defaults for leave, timesheets and reports. You can change it later in Settings.' }),
  team: () => ({ title: 'Invite your team', subtitle: 'Add emails, choose Permanent or Contractor and a role.' }),
  trial: () => ({ title: 'Your free trial', subtitle: 'Everything is unlocked for 14 days. After that, pay only for the people and resources you use.' }),
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [view, setView] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await onboarding.getOnboarding();
      setState(data);
      return data;
    } catch (e) {
      setError(errorMessage(e, 'Could not load your setup.'));
      return null;
    }
  }, []);

  useEffect(() => {
    load().then((data) => {
      if (!data) return;
      if (data.step !== 'done') setView(COPY[data.step] ? data.step : 'company');
      else refreshUser().then(() => navigate('/dashboard', { replace: true }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const goTo = async (step) => {
    try {
      await onboarding.setOnboardingStep(step);
    } catch (e) {
      setError(errorMessage(e, 'Could not save your progress.'));
      return;
    }
    await load();
    setView(step);
  };

  const showDone = async () => {
    await onboarding.completeOnboarding();
    const [, list] = await Promise.all([load(), onboarding.getChecklist()]);
    setChecklist(list.items);
    setView('done');
  };

  if (!state || !view) {
    return error ? <AuthShell step="company" title="Something went wrong" subtitle={error} /> : null;
  }

  const saveCompany = async (payload) => {
    try {
      await onboarding.saveCompanyDefaults(payload);
      await load();
      setView('team');
      return null;
    } catch (e) {
      return errorMessage(e, 'Could not save your company details.');
    }
  };

  const invite = async (invites) => {
    try {
      const result = await onboarding.inviteTeam(invites);
      await load();
      if (!result.skipped.length) setView('trial');
      return result;
    } catch (e) {
      return { error: errorMessage(e, 'Could not send the invites.') };
    }
  };

  const goToDashboard = async () => {
    await refreshUser();
    navigate('/dashboard', { replace: true });
  };

  if (view === 'done') {
    const sent = state.invited.permanent + state.invited.contractor;
    const ends = state.trial_ends_at && new Date(state.trial_ends_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
    return (
      <AuthShell
        step="done"
        brand={false}
        icon="✓"
        title={`${state.company.name} is ready`}
        subtitle={`You're signed in as the owner. ${sent} invite${sent === 1 ? ' has' : 's have'} been sent. Your trial runs until ${ends}.`}
      >
        <GetStartedChecklist items={checklist} />
        <div className={styles.actions} style={{ justifyContent: 'flex-start' }}>
          <button type="button" className={styles.primaryButton} onClick={goToDashboard}>Go to dashboard</button>
        </div>
      </AuthShell>
    );
  }

  const copy = COPY[view](state.company.name);
  return (
    <AuthShell step={view} title={copy.title} subtitle={copy.subtitle}>
      {error && <div className={styles.error} role="alert">{error}</div>}
      {view === 'company' && <CompanyStep company={state.company} onSave={saveCompany} onSkip={() => goTo('team')} />}
      {view === 'team' && <TeamStep onInvite={invite} onSkip={() => goTo('trial')} onBack={() => setView('company')} />}
      {view === 'trial' && (
        <TrialStep
          key={`${state.invited.permanent}-${state.invited.contractor}`}
          invited={state.invited}
          pricing={state.pricing}
          trialEndsAt={state.trial_ends_at}
          onStart={showDone}
          onBack={() => setView('team')}
        />
      )}
    </AuthShell>
  );
}
